/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { StripeElements } from '@stripe/stripe-js';

import { selectWebViewParams, selectWebViewTeamId } from 'containers/auth/authSlice';
import Loader from 'components/loader/Loader';
import StripeService from 'services/stripe';
import { useCreateEphemeralKeyMutation, useLazyGetCardByIdQuery } from 'containers/treasury/api';
import { ShowToastParams, WebviewFunctions } from 'constants/common';
import { logAnalyticEvent } from 'utils/analytics';
import { TreasuryCTEvents } from '../events';
import { CardData } from '../treasury.model';
import { WEBVIEW_NOTIFIER_TYPES } from 'constants/notifierConstants';
import { CopyIcon } from 'assets/icons';

interface CardViewProps {
  className?: string;
}

const CardView: React.FC<CardViewProps> = () => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const webViewTeamId = useAppSelector(selectWebViewTeamId);
  const { cardId: spendAccountCardId, spendAccountId, connectAccountId } = useAppSelector(selectWebViewParams);

  const stripePromise = useMemo(() => StripeService.getInstance(connectAccountId), [connectAccountId]);

  const stripeElementConfig = useMemo(
    () => ({
      style: {
        base: {
          fontFamily: '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: '24px',
          color: '#4F4F4F',
          fontWeight: '600'
        }
      }
    }),
    []
  );

  const stripeElementsRef = useRef<StripeElements | null>(null);
  const mountedElementsRef = useRef<Set<string>>(new Set());

  const [createEphemeralKey] = useCreateEphemeralKeyMutation();
  const [getCardById, { data: cardByIdData, isFetching: isFetchingCardDetails }] = useLazyGetCardByIdQuery();
  const { Data: cardDataFromApi } = cardByIdData || {};

  const handleLoadCardDetails = async (cardData: CardData) => {
    try {
      const stripe = await stripePromise;

      if (!stripe) {
        return;
      }

      const nonceResult = await stripe.createEphemeralKeyNonce({
        issuingCard: cardData?.cardProviderId
      });

      if (!nonceResult.nonce) {
        return;
      }

      const teamId = webViewTeamId || currentTeam?.id?.toString();
      if (!teamId) {
        return;
      }

      const result = await createEphemeralKey({
        spendAccountId: spendAccountId,
        spendAccountCardId: spendAccountCardId,
        nonce: nonceResult.nonce,
        teamId: teamId,
        cardId: cardData?.cardProviderId
      }).unwrap();

      if (!result?.Data?.ephemeralKeySecret) {
        return;
      }

      const elements = stripe.elements({
        fonts: [
          {
            cssSrc: '/fonts/lato-stripe.css'
          }
        ]
      });
      stripeElementsRef.current = elements;

      const elementConfig = {
        nonce: nonceResult.nonce,
        issuingCard: cardData?.cardProviderId,
        ephemeralKeySecret: result.Data.ephemeralKeySecret,
        ...stripeElementConfig
      };

      const elementPin = elements.create('issuingCardPinDisplay', elementConfig);

      elementPin.mount('#card-pin-display');
      mountedElementsRef.current.add('card-pin-display');

      if (window?.flutter_inappwebview?.callHandler) {
        window.flutter_inappwebview.callHandler(WebviewFunctions.WEBVIEW_LOADED);
      }
      const cardPinCopy = elements.create('issuingCardCopyButton', {
        toCopy: 'pin'
      });
      cardPinCopy.mount('#card-pin-copy');
      mountedElementsRef.current.add('card-pin-copy');
      cardPinCopy.on('click', () => {
        if (window?.flutter_inappwebview?.callHandler) {
          const toastParams: ShowToastParams = {
            message: 'Card pin copied',
            type: WEBVIEW_NOTIFIER_TYPES.SUCCESS
          };
          window.flutter_inappwebview.callHandler(WebviewFunctions.SHOW_TOAST, toastParams);
        }
      });
    } catch (error) {
      logAnalyticEvent(TreasuryCTEvents.CARD_VIEW_ERROR, {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  };

  const initialiseCardDetails = async () => {
    if (spendAccountCardId && spendAccountId) {
      const cardData = await getCardById({
        teamId: webViewTeamId || currentTeam?.id,
        spendAccountId,
        cardId: spendAccountCardId
      }).unwrap();
      if (cardData.Data) {
        await handleLoadCardDetails(cardData.Data);
      }
    }
  };

  useEffect(() => {
    initialiseCardDetails();
  }, [spendAccountCardId, spendAccountId, webViewTeamId]);

  // Cleanup function for Stripe elements
  const cleanupStripeElements = useCallback(() => {
    if (stripeElementsRef.current) {
      mountedElementsRef.current.forEach(elementId => {
        const element = document.getElementById(elementId);
        if (element) {
          element.innerHTML = '';
        }
      });
      mountedElementsRef.current.clear();
      stripeElementsRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupStripeElements();
    };
  }, [cleanupStripeElements]);

  if (isFetchingCardDetails || !cardDataFromApi) {
    return <Loader />;
  }

  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="flex items-center justify-center gap-2 rounded-md text-center">
        <span id="card-pin-display" className="w-16 font-lato" />
        <div className="relative flex items-center">
          <CopyIcon className={'path-fill-current h-4 w-4 text-primaryText'} aria-hidden="true" />
          <span
            id="card-pin-copy"
            className="absolute right-0 h-5 w-5 cursor-pointer"
            role="button"
            aria-label="Copy card pin to clipboard"
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(CardView);
